import { styled } from '@linaria/react';
import {
  baseWhite,
  inkDark,
  inkDarkest,
  inkLight,
  primaryBase,
  redBase,
  secondaryBase,
  secondaryLightest,
  skyDark,
  skyLight,
} from '../../../../styles/design-tokens';

/**
 * Clamps a percentage into the drawable range.
 * @param {number} value - Any percentage.
 * @returns {number} The value limited to 0..100.
 */
const clampPct = (value: number): number => Math.max(0, Math.min(100, value));

/**
 * Fallback label for a session with no title: the first characters of its id,
 * matching how the session header abbreviates ids.
 * @param {string} sessionId - The session id.
 * @returns {string} A short, stable label.
 */
const sessionFallbackTitle = (sessionId: string): string => sessionId.slice(0, 8);

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  background: ${baseWhite};
  border: 1px solid ${skyLight};
  border-radius: 16px;
  padding: 18px 20px;
`;

const SectionTitle = styled.span`
  font: 600 14px/1 'Figtree', sans-serif;
  color: ${inkDarkest};
`;

const SubtleText = styled.span`
  font: 500 12px/1.3 'Figtree', sans-serif;
  color: ${inkLight};
`;

const Additions = styled.span`
  font-family: 'SF Mono', ui-monospace, Menlo, monospace;
  color: ${primaryBase};
`;

const Deletions = styled.span`
  font-family: 'SF Mono', ui-monospace, Menlo, monospace;
  color: ${redBase};
`;

const Mono = styled.span`
  font-family: 'SF Mono', ui-monospace, Menlo, monospace;
`;

const FileName = styled.span`
  font: 600 11.5px/1.2 'SF Mono', ui-monospace, Menlo, monospace;
  color: ${inkDark};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StatePill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: 99px;
  border: 1px solid;
  font: 600 11px/1 'Figtree', sans-serif;
  text-transform: capitalize;
`;

const ThisBadge = styled.span`
  flex-shrink: 0;
  padding: 1px 6px;
  border-radius: 99px;
  background: ${secondaryLightest};
  color: ${secondaryBase};
  font: 600 9px/1.3 'Figtree', sans-serif;
`;

const NotTraced = styled.span`
  font: 500 11px/1 'Figtree', sans-serif;
  color: ${skyDark};
  font-style: italic;
`;

const LegendRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  font: 500 11px/1 'Figtree', sans-serif;
  color: ${inkLight};
`;

const LegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const LegendSwatch = styled.span<{ $color: string; $dashed?: boolean }>`
  width: 10px;
  height: 10px;
  border-radius: 2px;
  background: ${(props) => props.$color};
  border: ${(props) => (props.$dashed ? `1px dashed ${secondaryBase}` : 'none')};
`;

/** Text for assistive technology only; keeps meaning that is otherwise colour- or hover-only. */
const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

export {
  Additions,
  Card,
  clampPct,
  Deletions,
  FileName,
  LegendItem,
  LegendRow,
  LegendSwatch,
  Mono,
  NotTraced,
  SectionTitle,
  sessionFallbackTitle,
  StatePill,
  SubtleText,
  ThisBadge,
  VisuallyHidden,
};
